import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  CheckCircle, 
  User, 
  Stethoscope,
  MapPin,
  ArrowLeft,
  ArrowRight,
  Info,
  Video,
  Building
} from 'lucide-react';
import { 
  format, 
  addDays, 
  setHours, 
  setMinutes, 
  isBefore, 
  isAfter, 
  isSameDay,
  startOfDay,
  parseISO
} from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const SERVICE_ICONS = {
  consultation: '💬',
  procedure: '🏥',
  lab_test: '🔬',
  imaging: '📷',
  therapy: '🧘',
  preventive: '🛡️',
  specialty: '⭐',
  other: '📋'
};

const SPECIALTY_LABELS = {
  general_practice: 'General Practice',
  family_medicine: 'Family Medicine',
  internal_medicine: 'Internal Medicine',
  pediatrics: 'Pediatrics',
  cardiology: 'Cardiology',
  dermatology: 'Dermatology',
  neurology: 'Neurology',
  orthopedics: 'Orthopedics',
  psychiatry: 'Psychiatry',
  gynecology: 'Gynecology',
  urology: 'Urology',
  ophthalmology: 'Ophthalmology',
  ent: 'ENT',
  nutrition: 'Nutrition',
  physical_therapy: 'Physical Therapy',
  other: 'Other'
};

// Helper function to format provider display name avoiding title duplication
const formatProviderName = (title, fullName) => {
  if (!fullName) return '';
  
  // List of common title prefixes to check for duplication
  const titlePrefixes = ['Dr.', 'Dr', 'MD', 'DO', 'PhD', 'NP', 'PA', 'RN', 'PT', 'OD', 'DDS', 'DMD'];
  
  // Check if the name already starts with a title
  const nameLower = fullName.toLowerCase().trim();
  const startsWithTitle = titlePrefixes.some(prefix => 
    nameLower.startsWith(prefix.toLowerCase() + ' ') || 
    nameLower.startsWith(prefix.toLowerCase() + '.')
  );
  
  // If name already has a title, just return the name
  if (startsWithTitle) {
    return fullName;
  }
  
  // Otherwise, prepend the title
  return title ? `${title} ${fullName}` : fullName;
};

const EnhancedAppointmentScheduler = ({ member, onSuccess }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [appointmentMode, setAppointmentMode] = useState('in_person');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Service, 2: Provider, 3: Date & Time, 4: Confirm
  
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);

  useEffect(() => {
    fetchProvidersAndServices();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedProvider) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedProvider]);

  const fetchProvidersAndServices = async () => {
    try {
      setLoadingProviders(true);
      
      // Fetch real services from backend
      const [servicesRes, providersRes] = await Promise.all([
        axios.get(`${API}/appointments/services`),
        axios.get(`${API}/appointments/providers`)
      ]);
      
      // Transform services with icons
      const serviceData = servicesRes.data.map(service => ({
        ...service,
        icon: SERVICE_ICONS[service.category] || '📋',
        color: getServiceColor(service.category)
      }));
      
      // Transform providers
      const providerData = providersRes.data.map(provider => ({
        id: provider.id,
        name: provider.full_name,
        title: provider.title || 'MD',
        email: provider.email,
        specialty: SPECIALTY_LABELS[provider.specialty] || provider.specialty,
        specialtyKey: provider.specialty,
        services: provider.services || [],
        avatar: provider.full_name.split(' ').map(n => n[0]).join('').substring(0, 2),
        available: true,
        accepts_telemedicine: provider.accepts_telemedicine !== false,
        accepts_in_person: provider.accepts_in_person !== false
      }));

      setServices(serviceData);
      setProviders(providerData);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Fallback to default services if backend doesn't have any
      setServices([
        { id: 'general', name: 'Consulta General', description: 'Consulta médica general', duration_minutes: 30, category: 'consultation', icon: '💬', color: 'blue', appointment_mode: 'both' },
        { id: 'followup', name: 'Seguimiento', description: 'Cita de seguimiento', duration_minutes: 20, category: 'consultation', icon: '🔄', color: 'green', appointment_mode: 'both' }
      ]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const getServiceColor = (category) => {
    const colors = {
      consultation: 'blue',
      procedure: 'purple',
      lab_test: 'orange',
      imaging: 'cyan',
      therapy: 'green',
      preventive: 'teal',
      specialty: 'indigo',
      other: 'gray'
    };
    return colors[category] || 'gray';
  };

  const fetchAvailableSlots = async () => {
    try {
      // Fetch all appointments for the selected date and provider
      const response = await axios.get(`${API}/appointments`);
      const allAppointments = response.data;
      
      // Filter appointments for selected date and provider
      const dayAppointments = allAppointments.filter(apt => {
        const aptDate = parseISO(apt.appointment_date);
        return isSameDay(aptDate, selectedDate) && 
               apt.status !== 'cancelled';
      });

      // Extract booked time slots
      const booked = dayAppointments.map(apt => {
        const aptDate = parseISO(apt.appointment_date);
        return format(aptDate, 'HH:mm');
      });

      setBookedSlots(booked);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  // Generate time slots based on service duration
  const generateTimeSlots = () => {
    if (!selectedService) return [];
    
    const slots = [];
    const duration = selectedService.duration_minutes || selectedService.duration || 30;
    const startHour = 9;
    const endHour = 17;
    
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += duration) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const isBooked = bookedSlots.includes(timeStr);
        const isPastTime = isBefore(
          setHours(setMinutes(selectedDate, minute), hour),
          new Date()
        );
        
        slots.push({
          hour,
          minute,
          label: format(setHours(setMinutes(new Date(), minute), hour), 'h:mm a'),
          time: timeStr,
          available: !isBooked && !isPastTime
        });
      }
    }
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Filter providers based on selected service
  const getFilteredProviders = () => {
    if (!selectedService) return providers;
    
    // Filter by service ID if provider has services defined
    return providers.filter(provider => {
      // If no services defined, show all providers
      if (!provider.services || provider.services.length === 0) return true;
      return provider.services.includes(selectedService.id);
    });
  };

  const filteredProviders = getFilteredProviders();

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setStep(2);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setStep(3);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (slot) => {
    if (selectedDate && slot.available) {
      const appointmentDateTime = setHours(setMinutes(selectedDate, slot.minute), slot.hour);
      setSelectedTime(appointmentDateTime);
      setStep(4);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !member || !selectedService) {
      toast.error('Please complete all required fields');
      return;
    }

    setLoading(true);
    try {
      const duration = selectedService.duration_minutes || selectedService.duration || 30;
      
      await axios.post(`${API}/appointments`, {
        member_id: member.id,
        appointment_date: selectedTime.toISOString(),
        appointment_type: selectedService.category || 'general',
        appointment_mode: appointmentMode,
        duration_minutes: duration,
        status: 'pending',
        reason: notes || undefined,
        provider_id: selectedProvider?.id,
        service_id: selectedService.id
      });

      toast.success('Appointment booked successfully!');
      setStep(5);
      
      if (onSuccess) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error booking appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedProvider(null);
    setSelectedService(null);
    setNotes('');
    setStep(1);
  };

  // Success confirmation screen
  if (step === 5) {
    return (
      <Card className="max-w-3xl mx-auto">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-6">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Appointment Confirmed!</h2>
            <p className="text-gray-600">
              Your appointment has been successfully scheduled. You will receive a confirmation email shortly.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-teal-200">
                <div className="flex items-center gap-2 text-gray-600">
                  <CalendarIcon className="h-5 w-5" />
                  <span>Date & Time</span>
                </div>
                <span className="font-semibold text-gray-900">
                  {format(selectedTime, 'EEEE, MMM dd, yyyy')}
                  <br />
                  <span className="text-teal-600">{format(selectedTime, 'h:mm a')}</span>
                </span>
              </div>
              
              <div className="flex items-center justify-between pb-3 border-b border-teal-200">
                <div className="flex items-center gap-2 text-gray-600">
                  <Stethoscope className="h-5 w-5" />
                  <span>Service</span>
                </div>
                <span className="font-semibold text-gray-900">{selectedService.name}</span>
              </div>

              {selectedProvider && (
                <div className="flex items-center justify-between pb-3 border-b border-teal-200">
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="h-5 w-5" />
                    <span>Provider</span>
                  </div>
                  <span className="font-semibold text-gray-900">{selectedProvider.name}</span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-5 w-5" />
                  <span>Duration</span>
                </div>
                <span className="font-semibold text-gray-900">{selectedService.duration_minutes || selectedService.duration || 30} minutes</span>
              </div>

              {notes && (
                <div className="pt-3 border-t border-teal-200">
                  <span className="text-gray-600 block mb-2">Notes:</span>
                  <p className="text-sm text-gray-700 bg-white rounded-lg p-3">{notes}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={handleReset} size="lg">
              Book Another Appointment
            </Button>
            <Button onClick={onSuccess} className="bg-teal-600 hover:bg-teal-700" size="lg">
              View All Appointments
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto">
      {/* Progress Steps - Mobile Optimized */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Service', shortLabel: 'Svc', icon: Stethoscope },
            { num: 2, label: 'Provider', shortLabel: 'Doc', icon: User },
            { num: 3, label: 'Date & Time', shortLabel: 'Time', icon: CalendarIcon },
            { num: 4, label: 'Confirm', shortLabel: 'OK', icon: CheckCircle }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s.num 
                    ? 'bg-teal-600 text-white shadow-lg' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  <s.icon className="h-4 w-4 sm:h-6 sm:w-6" />
                </div>
                <span className={`text-xs sm:text-sm mt-1 sm:mt-2 font-medium text-center ${
                  step >= s.num ? 'text-teal-600' : 'text-gray-500'
                }`}>
                  <span className="hidden sm:inline">{s.label}</span>
                  <span className="sm:hidden">{s.shortLabel}</span>
                </span>
              </div>
              {idx < 3 && (
                <div className={`h-0.5 sm:h-1 flex-1 mx-1 sm:mx-4 rounded ${
                  step > s.num ? 'bg-teal-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Select Service */}
      {step === 1 && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-2xl">
              <Stethoscope className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600" />
              Select a Service
            </CardTitle>
            <CardDescription className="text-sm">Choose the type of care you need</CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            {loadingProviders ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading services...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className={`p-4 sm:p-6 border-2 rounded-xl text-left transition-all hover:shadow-lg group relative ${
                      service.is_included_in_plan 
                        ? 'border-teal-300 bg-teal-50/30 hover:border-teal-500' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {/* Membership Badge */}
                    {service.is_included_in_plan && (
                      <div className="absolute -top-2 -right-2">
                        <Badge className="bg-teal-600 text-white text-xs">
                          ✓ Included
                        </Badge>
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-4xl">{service.icon}</div>
                      <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                        {service.duration_minutes || service.duration} min
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                    
                    {/* Membership Availability Info */}
                    {service.is_included_in_plan && service.quantity_allowed > 0 && (
                      <div className="mt-2 pt-2 border-t border-teal-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Available:</span>
                          <span className={`font-semibold ${
                            service.quantity_remaining === 0 
                              ? 'text-red-600' 
                              : service.quantity_remaining === 1 
                                ? 'text-amber-600' 
                                : 'text-teal-600'
                          }`}>
                            {service.quantity_remaining} of {service.quantity_allowed} 
                            <span className="text-gray-500 font-normal ml-1">
                              / {service.period_type === 'month' ? 'month' : service.period_type === 'year' ? 'year' : 'plan'}
                            </span>
                          </span>
                        </div>
                        {service.quantity_remaining === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            ⚠️ Quota reached - additional charges may apply
                          </p>
                        )}
                        {service.quantity_scheduled > 0 && (
                          <p className="text-xs text-blue-600 mt-1">
                            📅 {service.quantity_scheduled} already scheduled
                          </p>
                        )}
                      </div>
                    )}
                    
                    {service.is_included_in_plan && service.quantity_allowed === -1 && (
                      <div className="mt-2 pt-2 border-t border-teal-200">
                        <span className="text-sm text-teal-600 font-medium">✨ Unlimited</span>
                      </div>
                    )}
                    
                    {!service.is_included_in_plan && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <span className="text-xs text-gray-500">Not included in your plan - charges apply</span>
                      </div>
                    )}
                    
                    {service.appointment_mode && service.appointment_mode !== 'both' && (
                      <Badge variant="secondary" className="mt-2">
                        {service.appointment_mode === 'telemedicine' ? '📹 Telemedicine' : '🏥 In-Person'}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Provider & Mode */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <User className="h-6 w-6 text-teal-600" />
                  Select Your Provider
                </CardTitle>
                <CardDescription>
                  Choose a healthcare provider for your {selectedService.name}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Change Service
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Appointment Mode Selection */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-base font-medium mb-3 block">Appointment Mode</Label>
              <RadioGroup value={appointmentMode} onValueChange={setAppointmentMode} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="in_person" id="in_person" />
                  <Label htmlFor="in_person" className="flex items-center gap-2 cursor-pointer">
                    <Building className="h-4 w-4" />
                    In-Person
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="telemedicine" id="telemedicine" />
                  <Label htmlFor="telemedicine" className="flex items-center gap-2 cursor-pointer">
                    <Video className="h-4 w-4" />
                    Telemedicine
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Providers List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredProviders.length === 0 ? (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No providers available at this time</p>
                </div>
              ) : (
                filteredProviders.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderSelect(provider)}
                    className="p-6 border-2 border-gray-200 rounded-xl text-left transition-all hover:border-teal-500 hover:shadow-lg flex items-start gap-4"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                      {provider.avatar}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">
                        {formatProviderName(provider.title, provider.name)}
                      </h3>
                      <p className="text-sm text-teal-600 mb-2">{provider.specialty}</p>
                      <div className="flex gap-2 mt-2">
                        {provider.accepts_in_person && (
                          <Badge variant="outline" className="text-xs">
                            <Building className="h-3 w-3 mr-1" /> In-Person
                          </Badge>
                        )}
                        {provider.accepts_telemedicine && (
                          <Badge variant="outline" className="text-xs">
                            <Video className="h-3 w-3 mr-1" /> Virtual
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="mt-6 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                All our providers are highly qualified and ready to assist you with your healthcare needs.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Select Date & Time */}
      {step === 3 && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Date Selection */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-teal-600" />
                  Select Date
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </div>
              <CardDescription>Choose your preferred appointment date</CardDescription>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => 
                  isBefore(date, startOfDay(new Date())) || 
                  date.getDay() === 0 || // Sundays
                  date.getDay() === 6    // Saturdays
                }
                className="rounded-md border mx-auto"
              />
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-teal-600"></div>
                  <span className="text-gray-600">Available</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                  <span className="text-gray-600">Unavailable (Weekends)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Time Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-teal-600" />
                Select Time
              </CardTitle>
              <CardDescription>
                {selectedDate 
                  ? format(selectedDate, 'EEEE, MMMM dd, yyyy')
                  : 'Please select a date first'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!selectedDate ? (
                <div className="text-center py-12 text-gray-400">
                  <Clock className="h-16 w-16 mx-auto mb-3" />
                  <p>Select a date to view available times</p>
                </div>
              ) : timeSlots.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Clock className="h-16 w-16 mx-auto mb-3" />
                  <p>Loading available times...</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                  {timeSlots.map((slot) => (
                    <Button
                      key={`${slot.hour}-${slot.minute}`}
                      variant={slot.available ? "outline" : "ghost"}
                      className={`h-12 ${
                        slot.available 
                          ? 'hover:bg-teal-50 hover:border-teal-500 hover:text-teal-700' 
                          : 'opacity-40 cursor-not-allowed'
                      }`}
                      onClick={() => handleTimeSelect(slot)}
                      disabled={!slot.available}
                    >
                      {slot.label}
                    </Button>
                  ))}
                </div>
              )}
              
              {selectedDate && timeSlots.length > 0 && (
                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-300"></div>
                    <span className="text-gray-600">Available</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    <span className="text-gray-600">Booked</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {step === 4 && (
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-lg sm:text-2xl">Confirm Appointment</CardTitle>
                <CardDescription className="text-sm">Review details and add notes</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep(3)} className="self-start sm:self-auto">
                <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Change </span>Time
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6 pt-0">
            {/* Summary */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600" />
                Appointment Summary
              </h3>
              <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold text-gray-900 text-right truncate ml-2">{selectedService.name}</span>
                </div>
                {selectedProvider && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Provider:</span>
                    <span className="font-semibold text-gray-900 text-right truncate ml-2">{selectedProvider.name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Date & Time:</span>
                  <span className="font-semibold text-teal-600 text-right text-xs sm:text-base">
                    {format(selectedTime, 'MMM dd, yyyy')}
                    <br className="sm:hidden" />
                    <span className="hidden sm:inline"> • </span>
                    {format(selectedTime, 'h:mm a')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-semibold text-gray-900">{selectedService.duration_minutes || selectedService.duration || 30} min</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-sm sm:text-base">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any concerns or requests for your provider..."
                rows={3}
                className="mt-2 text-sm sm:text-base"
              />
            </div>

            {/* Info Box - Compact on mobile */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 flex gap-2 sm:gap-3">
              <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs sm:text-sm text-blue-800">
                <p className="font-medium mb-1">Please note:</p>
                <ul className="list-disc list-inside space-y-0.5 sm:space-y-1">
                  <li>Confirmation email within 15 min</li>
                  <li>Arrive 10 min early</li>
                  <li>Cancel 24h in advance</li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                className="flex-1 text-sm sm:text-base"
                size="default"
              >
                <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-sm sm:text-base"
                size="default"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span className="hidden xs:inline">Booking...</span>
                    <span className="xs:hidden">...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Confirm Appointment</span>
                    <span className="sm:hidden">Confirm</span>
                    <ArrowRight className="h-4 w-4 ml-1 sm:ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnhancedAppointmentScheduler;
